import { useCallback } from 'react';
import { bridge, isNotNil, isWeb } from 'shared/src/utils';
import { registerHostBridgeCallback } from 'shared/src/utils/hostBridgeCallback/hostBridgeCallback';
import { useInstanceId } from './useInstanceId';
import { useLogAnalyticsEvent } from './useLogAnalyticsEvent';
import { useOpenDeeplink } from './useOpenDeeplink';
import { useRefreshToday } from './useRefreshToday';
import { useRootStackNavigation } from './useRootNavigation';

type WebViewMessage = {
  type: string;
  body?: string | object;
};

/**
 * 로그인 후 돌아올 현재 주소 — path + query 를 그대로 잇는다(`/goods/123?list_id=abc`).
 *
 * pathname 만 쓰면 쿼리가 날아가, 로그인 복귀 시 목록/광고 귀속 파라미터(list_id 등)를
 * 잃는다. hash 는 이 앱의 라우팅에 쓰이지 않아 제외한다.
 *
 * 네이티브에는 `location` 이 없으므로 undefined 를 반환한다(참조 자체가 에러라 typeof 로 확인).
 */
const getCurrentPathWithQuery = (): string | undefined => {
  if (typeof location === 'undefined') return undefined;
  return `${location.pathname}${location.search}`;
};

export const useWebViewActionFromMessage = (params?: {
  onChangeTitle?: (title: string) => void;
}) => {
  const navigation = useRootStackNavigation();
  const { openDeeplink } = useOpenDeeplink();
  const instanceId = useInstanceId();
  const logAnalyticsEvent = useLogAnalyticsEvent();
  const refreshToday = useRefreshToday();

  const convertEventToData = useCallback<
    (params: { nativeEvent: { data?: string } }) => WebViewMessage | null
  >(({ nativeEvent }) => {
    try {
      const message = JSON.parse(nativeEvent.data ?? '{}');

      if (typeof message.type === 'undefined') return null;

      return message;
    } catch {
      return null;
    }
  }, []);

  const convertToObject = useCallback((body?: string | object) => {
    if (isNotNil(body)) {
      try {
        return typeof body === 'string' ? JSON.parse(body) : body;
      } catch {
        return null;
      }
    }

    return null;
  }, []);

  return useCallback<
    (params: {
      nativeEvent: { data?: string };
      /** [web 전용] 메시지를 보낸 iframe. 결과를 돌려줘야 하는 브릿지에서 응답 대상으로 쓴다. */
      source?: Window;
      /** [web 전용] 그 iframe 의 오리진. */
      origin?: string;
    }) => void
  >(
    event => {
      const message = convertEventToData(event);
      console.log(message);

      if (isNotNil(message)) {
        switch (message.type) {
          // iframe 웹뷰 콘텐츠(ably-webview-react)가 embedded-web 에서 페이지 이동 대신
          // 부모로 올려보낸 딥링크를 앱 레벨에서 라우팅한다(= iframe 안에서 이동이 끝나던 문제 해소).
          case 'openDeeplink': {
            const body = convertToObject(message.body);
            if (isNotNil(body) && isNotNil(body.deeplink)) {
              openDeeplink(body.deeplink);
            }
            return;
          }
          // iframe 콘텐츠가 로그인 필요를 알리면 앱 레벨 로그인 처리.
          // (web: bridge.assertLogin 이 /login 으로 이동, native: 네이티브 로그인)
          case 'assertLogin': {
            const body = convertToObject(message.body);
            bridge.assertLogin({
              returnDeeplink: body?.returnDeeplink,
              redirect: body?.redirect ?? getCurrentPathWithQuery(),
              message: body?.message,
            });
            return;
          }
          // iframe 콘텐츠의 로깅을 앱 레벨로 올려 보낸다(웹뷰 안에서 끊기지 않게).
          // 채널/이벤트명/파라미터는 콘텐츠가 정하고, 호스트 화면의 AnalyticsContext 는
          // useLogAnalyticsEvent 가 빈 자리에만 머지한다(콘텐츠가 보낸 값이 우선).
          // 콘텐츠는 ably_only_log 를 `?? null` 로 보내므로 null 은 undefined 로 떨어뜨린다.
          case 'logAnalyticsEvent': {
            const body = convertToObject(message.body);
            if (isNotNil(body) && isNotNil(body.name)) {
              logAnalyticsEvent({
                name: body.name,
                channel: Array.isArray(body.channel) ? body.channel : undefined,
                analytics: body.analytics ?? undefined,
                ably_only_log: body.ably_only_log ?? undefined,
              });
            }
            return;
          }
          // 배송지 선택창 열기. 네이티브는 `openDeliveryList` 핸들러가 화면을 띄우고
          // `ok_function_name` 전역 함수로 결과를 돌려주는데, iframe 콘텐츠에는 그 전역이
          // 없다. 그래서 호스트가 AddressList(select 모드)를 열고, 선택이 확정되면 그
          // 화면이 `resolveHostBridgeCallback` 으로 이 iframe 에 결과를 postMessage 한다.
          case 'openDeliveryList': {
            const body = convertToObject(message.body);
            const callbackName = body?.ok_function_name;

            // 응답 대상(iframe)을 모르면 결과를 돌려줄 방법이 없다. 화면만 열어두면 사용자가
            // 선택을 마쳐도 콘텐츠는 영원히 기다리므로, 아무것도 하지 않는다.
            if (!isWeb || !isNotNil(callbackName) || event.source == null) {
              return;
            }

            registerHostBridgeCallback(
              callbackName,
              event.source,
              event.origin ?? window.location.origin,
            );

            navigation.push('AddressList', {
              mode: 'select',
              okFunctionName: callbackName,
            });
            return;
          }
          // 콘텐츠가 오늘 지면 결과를 바꾼 뒤(취향 온보딩 등) 갱신을 요청한다. 오늘 지면은
          // RN 렌더(`TodayAtomic`)라 관련 react-query 캐시를 무효화하면 다시 그려진다.
          //
          // ⚠️ `scroll_to_filter` 는 처리하지 않는다. 네이티브 오늘 지면 전용 파라미터다.
          case 'refreshToday': {
            const body = convertToObject(message.body);

            refreshToday();

            if (isNotNil(body) && isNotNil(body.toast)) {
              bridge.toast(body.toast);
            }
            return;
          }
          case 'setTitle': {
            const body = convertToObject(message.body);
            if (isNotNil(body)) {
              params?.onChangeTitle?.(body?.title ?? '');
            }

            return;
          }
          case 'closeWebview': {
            const canGoBack = navigation.canGoBack();

            if (canGoBack) {
              navigation.goBack();
            } else if (isWeb) {
              // 뒤로 갈 곳이 없는 웹 진입(직접 URL·공유 링크 등)이면 스택을 'main' 하나로 리셋한다.
              // reset 은 화면별 params 타입의 union 이라 함수 타입만 넓혀 호출한다(push 와 동일).
              navigation.reset({ index: 0, routes: [{ name: 'tab' }] });
            } else {
              bridge.closeInstance({ instanceId });
            }

            const body = convertToObject(message.body);
            if (isNotNil(body) && isNotNil(body.afterDeeplink)) {
              openDeeplink(body.afterDeeplink);
            }

            return;
          }
        }
      }
    },
    [
      convertEventToData,
      convertToObject,
      openDeeplink,
      params,
      navigation,
      instanceId,
      logAnalyticsEvent,
      refreshToday,
    ],
  );
};
